<script setup lang="ts">
import {
  CalendarCheck,
  MessageCircle,
  Megaphone,
  Users,
  CheckCircle2,
  ArrowLeft,
  Menu
} from 'lucide-vue-next';
import { trackEvent } from '@app/composables/posthog';
import { appStoreUrl, googlePlayUrl } from '@app/consts/store-links';
import { goTo } from '@app/utils';

const goToRegister = (): void => {
  trackEvent('hero_register_clicked', { source: 'hero' });
  goTo('register');
};

const goToLogin = (): void => {
  trackEvent('hero_login_clicked');
  goTo('admin-login')
};
</script>

<template>
  <section id="hero" class="relative pt-28 pb-16 lg:pt-40 lg:pb-28 overflow-hidden">
    <div class="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full opacity-50 pointer-events-none" style="background: radial-gradient(circle, rgba(251,207,232,0.5) 0%, transparent 70%)"></div>
    <div class="absolute top-40 left-0 -ml-20 w-72 h-72 rounded-full opacity-50 pointer-events-none" style="background: radial-gradient(circle, rgba(233,213,255,0.5) 0%, transparent 70%)"></div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">

        <!-- Text Content -->
        <div class="max-w-2xl mx-auto lg:mx-0 text-center lg:text-right">
          <h1 class="text-3xl sm:text-5xl md:text-6xl font-black text-slate-900 leading-[1.15] sm:leading-[1.1] mb-6">
            תפסיקו לרדוף אחרי תורים ולקוחות, <br class="hidden sm:block" />
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">ותחזרו ליהנות מהעסק שלכם.</span>
          </h1>
          <p class="text-base sm:text-xl text-slate-600 mb-6 sm:mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0">
            טיימאיט היא לא סתם עוד יומן או אפליקציה. היא המנהלת האישית שלכם שדואגת שהלקוחות יגיעו בזמן, שהכסף ייכנס, שהעסק יצמח ושלכם יישאר אוויר לנשימה. הכל במרחק נגיעה.
          </p>

          <!-- Primary CTA: registration -->
          <button
            @click="goToRegister"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white text-lg sm:text-xl px-8 sm:px-12 py-4 sm:py-5 rounded-full font-bold transition-[transform,background] shadow-xl shadow-pink-500/30 hover:-translate-y-0.5"
          >
            התחילו 14 יום חינם
            <ArrowLeft class="w-5 h-5 shrink-0" />
          </button>

          <p class="mt-3 text-xs sm:text-sm text-slate-500 flex items-center justify-center lg:justify-start gap-1">
            <CheckCircle2 class="w-4 h-4 text-green-500 shrink-0" /> ללא כרטיס אשראי · ביטול בכל עת
          </p>

          <p class="mt-4 text-sm text-slate-500 text-center lg:text-right">
            כבר יש לכם חשבון?
            <button @click="goToLogin" class="text-pink-600 hover:text-pink-700 hover:underline font-medium inline-flex items-center gap-1 align-middle">
              התחברו עכשיו <ArrowLeft class="w-4 h-4" />
            </button>
          </p>

          <!-- Secondary: app download, for people who already have an account -->
          <div class="mt-8 pt-6 border-t border-slate-200/80">
            <p class="text-xs text-slate-400 mb-2 text-center lg:text-right">כבר רשומים? הורידו את האפליקציה</p>
            <div class="flex flex-row justify-center lg:justify-start gap-2">
              <a target="_blank" :href="appStoreUrl('hero')" @click="trackEvent('app_store_clicked', { source: 'hero' })" class="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-2.5 py-1.5 rounded-lg transition-colors">
                <svg viewBox="0 0 384 512" class="w-4 h-4 fill-current shrink-0"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                <span class="text-[11px] font-semibold leading-none font-sans" dir="ltr">App Store</span>
              </a>
              <a target="_blank" :href="googlePlayUrl('hero')" @click="trackEvent('google_play_clicked', { source: 'hero' })" class="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-2.5 py-1.5 rounded-lg transition-colors">
                <svg viewBox="0 0 512 512" class="w-4 h-4 fill-current shrink-0"><path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/></svg>
                <span class="text-[11px] font-semibold leading-none font-sans" dir="ltr">Google Play</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Visual / App Mockup -->
        <div class="relative mx-auto w-full max-w-[280px] sm:max-w-[320px] lg:max-w-none flex justify-center perspective-1000 mt-8 lg:mt-0">
          <div class="relative border-gray-900 dark:border-gray-900 bg-gray-900 border-[10px] sm:border-[12px] rounded-[2.5rem] sm:rounded-[3rem] h-[550px] sm:h-[600px] w-full sm:w-[280px] shadow-2xl rotate-[-2deg] transform transition-transform hover:rotate-0 duration-500">
            <div class="w-[100px] sm:w-[120px] h-[16px] sm:h-[18px] bg-gray-900 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute z-20"></div>
            <div class="h-[46px] w-[3px] bg-gray-900 absolute -left-[13px] sm:-left-[15px] top-[124px] rounded-l-lg"></div>
            <div class="h-[46px] w-[3px] bg-gray-900 absolute -left-[13px] sm:-left-[15px] top-[178px] rounded-l-lg"></div>
            <div class="h-[64px] w-[3px] bg-gray-900 absolute -right-[13px] sm:-right-[15px] top-[142px] rounded-r-lg"></div>

            <!-- Screen Content -->
            <div class="rounded-[2rem] sm:rounded-[2.25rem] overflow-hidden w-full h-full bg-slate-50 relative flex flex-col">
              <!-- Mockup Header -->
              <div class="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-5 sm:p-6 pt-10 pb-6 sm:pb-8 rounded-b-3xl shadow-sm z-10">
                <div class="flex justify-between items-center mb-4">
                  <div class="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center bg-white/30">
                    <span class="font-bold text-sm sm:text-base">ש"ק</span>
                  </div>
                  <div class="flex gap-2">
                    <div class="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center bg-white/30">
                      <MessageCircle class="w-4 h-4" />
                    </div>
                  </div>
                </div>
                <h3 class="font-bold text-xl sm:text-2xl mb-1">היי שירן 👋</h3>
                <p class="text-pink-100 text-xs sm:text-sm opacity-90">איזה כיף, יום מלא לפנייך!</p>

                <div class="mt-4 sm:mt-6 bg-white/25 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex justify-between items-center border border-white/10">
                  <div>
                    <p class="text-white/80 text-[10px] sm:text-xs mb-1">הכנסות היום</p>
                    <p class="font-bold text-lg sm:text-xl">₪1,450</p>
                  </div>
                  <div class="h-8 w-[1px] bg-white/30"></div>
                  <div>
                    <p class="text-white/80 text-[10px] sm:text-xs mb-1">תורים להיום</p>
                    <p class="font-bold text-lg sm:text-xl">6 תורים</p>
                  </div>
                </div>
              </div>

              <!-- Appointments List -->
              <div class="flex-1 p-4 -mt-4 overflow-hidden relative z-0">
                <div class="flex justify-between items-center mb-4 mt-6">
                  <h4 class="font-bold text-slate-800 text-sm sm:text-base">התורים הקרובים</h4>
                  <span class="text-pink-600 text-[10px] sm:text-xs font-semibold">לוח זמנים מלא</span>
                </div>

                <div class="space-y-3">
                  <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-sm border border-slate-100 flex items-center gap-3 sm:gap-4">
                    <div class="bg-pink-50 text-pink-600 font-bold w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-col leading-tight shrink-0">
                      <span class="text-xs sm:text-sm">10</span>
                      <span class="text-[10px] sm:text-xs">00</span>
                    </div>
                    <div class="flex-1 overflow-hidden">
                      <p class="font-bold text-slate-800 text-xs sm:text-sm truncate">נועה לוי</p>
                      <p class="text-[10px] sm:text-xs text-slate-500 truncate">מילוי ציפורניים קלאסי</p>
                    </div>
                    <div class="bg-green-100 text-green-700 p-1.5 rounded-full shrink-0">
                      <CheckCircle2 class="w-3 h-3 sm:w-4 sm:h-4" />
                    </div>
                  </div>

                  <div class="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-sm border border-slate-100 flex items-center gap-3 sm:gap-4 relative overflow-hidden">
                    <div class="absolute left-0 top-0 bottom-0 w-1 bg-orange-400"></div>
                    <div class="bg-orange-50 text-orange-600 font-bold w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-col leading-tight shrink-0">
                      <span class="text-xs sm:text-sm">11</span>
                      <span class="text-[10px] sm:text-xs">30</span>
                    </div>
                    <div class="flex-1 overflow-hidden">
                      <p class="font-bold text-slate-800 text-xs sm:text-sm truncate">יעל כהן</p>
                      <p class="text-[10px] sm:text-xs text-slate-500 truncate">לק ג'ל + מבנה אנטומי</p>
                    </div>
                    <div class="bg-orange-100 text-orange-700 text-[9px] sm:text-[10px] font-bold px-2 py-1 rounded-full shrink-0">
                      ממתינה
                    </div>
                  </div>

                  <div class="bg-slate-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-dashed border-slate-200 flex items-center gap-3 sm:gap-4">
                    <div class="text-slate-400 font-bold w-10 sm:w-12 text-center leading-tight shrink-0">
                      <span class="text-xs sm:text-sm block">13:00</span>
                    </div>
                    <div class="flex-1 overflow-hidden">
                      <p class="text-slate-500 text-[10px] sm:text-xs font-medium truncate">זמן לעצמך (הפסקת צהריים)</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bottom Nav Bar -->
              <div class="bg-white border-t border-slate-100 px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center z-10">
                <div class="text-pink-600 flex flex-col items-center gap-1">
                  <CalendarCheck class="w-5 h-5 sm:w-6 sm:h-6" />
                  <span class="text-[9px] sm:text-[10px] font-bold">יומן</span>
                </div>
                <div class="text-slate-400 flex flex-col items-center gap-1">
                  <Users class="w-5 h-5 sm:w-6 sm:h-6" />
                  <span class="text-[9px] sm:text-[10px]">לקוחות</span>
                </div>
                <div class="text-slate-400 flex flex-col items-center gap-1">
                  <Megaphone class="w-5 h-5 sm:w-6 sm:h-6" />
                  <span class="text-[9px] sm:text-[10px]">שיווק</span>
                </div>
                <div class="text-slate-400 flex flex-col items-center gap-1">
                  <Menu class="w-5 h-5 sm:w-6 sm:h-6" />
                  <span class="text-[9px] sm:text-[10px]">עוד</span>
                </div>
              </div>

              <!-- Floating Action Button -->
              <div class="absolute bottom-16 sm:bottom-20 left-1/2 -translate-x-1/2">
                <button class="bg-slate-900 text-white p-2 sm:p-3 rounded-full shadow-lg shadow-slate-900/30">
                  <svg class="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"></path></svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Decorative floating elements -->
          <div class="absolute -right-12 md:right-4 top-1/4 bg-white p-2 pe-3 sm:p-3 rounded-2xl shadow-xl flex items-center gap-2 sm:gap-3 animate-float scale-90 sm:scale-100 origin-right z-20" style="animation-duration: 3s;">
            <div class="bg-green-100 p-1.5 sm:p-2 rounded-full text-green-600 shrink-0">
              <CheckCircle2 class="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <p class="text-[9px] sm:text-xs text-slate-500 leading-tight">יופי! תור חדש נקבע</p>
              <p class="font-bold text-xs sm:text-sm text-slate-800 leading-tight mt-0.5">היום ב-15:00</p>
            </div>
          </div>

          <div class="absolute -left-8 pe-3 sm:-left-8 bottom-1/4 bg-white p-2 sm:p-3 rounded-2xl shadow-xl flex items-center gap-2 sm:gap-3 animate-float scale-90 sm:scale-100 origin-left z-20" style="animation-duration: 4s; animation-delay: 1s;">
            <div class="bg-pink-100 p-1.5 sm:p-2 rounded-full text-pink-600 shrink-0">
              <MessageCircle class="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <p class="font-bold text-xs sm:text-sm text-slate-800 leading-tight">דאגנו למחר</p>
              <p class="text-[9px] sm:text-xs text-slate-500 leading-tight mt-0.5">נשלחו 12 תזכורות ללקוחות</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
